/**
 * Pre-LODR Tab — Session 147j+17q (Feb 2026).
 *
 * Read-only feed of PUBLIC tender-portal awards (NHAI / PowerGrid /
 * NTPC / MoD / SECI / IREPS ...) that let a reader see order news
 * before the listed company files LODR with the exchange.
 *
 * INFORMATION-ONLY per user spec — no verdicts, no trading signals,
 * no risk column. The user decides what to do with L1 vs LoA vs
 * Contract-Signed events.
 *
 * Fully independent of other tabs (no cross-tab state, no shared
 * events). Backend: GET /api/pre-lodr/events + /api/pre-lodr/summary.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowUp, ArrowDown, ExternalLink, Radar, CheckCircle2, Clock, HelpCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SOURCES = ['ALL', 'NHAI', 'PowerGrid', 'NTPC', 'MoD', 'SECI', 'IREPS'];
const STAGES = ['ALL', 'L1', 'LoA', 'Contract Signed'];
const MATCH_STATUS = ['ALL', 'matched', 'pending', 'unmatched'];

const STAGE_TONE = {
  'L1':               'bg-amber-500/20 text-amber-200 border border-amber-500/40',
  'LoA':              'bg-blue-500/20 text-blue-200 border border-blue-500/40',
  'Contract Signed':  'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40',
};

const MATCH_ICON = {
  matched:   <CheckCircle2 size={11} className="inline text-emerald-400" />,
  pending:   <Clock         size={11} className="inline text-amber-300" />,
  unmatched: <HelpCircle    size={11} className="inline text-zinc-500" />,
};

const fmtCr = (v) => (v == null ? '—' : `₹${Number(v).toFixed(1)} Cr`);
const fmtDT = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};
const fmtLag = (hrs) => {
  if (hrs == null) return '—';
  const h = Number(hrs);
  if (Math.abs(h) < 1) return `${Math.round(h * 60)}m`;
  if (Math.abs(h) < 48) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
};

// ─── KPI strip ────────────────────────────────────────────────
const KPI = ({ label, value, sub, tone, testId }) => (
  <div className="rounded border border-zinc-800 bg-zinc-950/60 p-3 min-w-[140px]" data-testid={testId}>
    <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`text-xl font-bold ${tone || 'text-zinc-100'}`}>{value}</div>
    {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

const KpiStrip = ({ summary, events }) => {
  if (!summary) return null;
  const totalCr = events.reduce((a, e) => a + (e.order_value_cr || 0), 0);
  return (
    <div className="flex flex-wrap gap-3" data-testid="prelodr-kpi-strip">
      <KPI testId="prelodr-kpi-total" label="Total events" value={summary.total_events ?? 0} />
      <KPI testId="prelodr-kpi-matched" label="LODR matched"
           value={summary.matched_events ?? 0}
           tone="text-emerald-400"
           sub={summary.total_events ? `${((summary.matched_events / summary.total_events) * 100).toFixed(0)}% mapped` : ''} />
      <KPI testId="prelodr-kpi-pending" label="Pending match"
           value={summary.pending_events ?? 0}
           tone="text-amber-300" />
      <KPI testId="prelodr-kpi-unmapped" label="Unmapped vendor"
           value={summary.unmatched_events ?? 0}
           tone="text-zinc-400" />
      <KPI testId="prelodr-kpi-value" label="Total ₹Cr (shown)" value={fmtCr(totalCr)} />
    </div>
  );
};

// ─── Table ─────────────────────────────────────────────────────
const HEADERS = [
  { id: 'detected_at',        label: 'Detected At',       numeric: false },
  { id: 'source',             label: 'Source',            numeric: false },
  { id: 'customer',           label: 'Customer',          numeric: false },
  { id: 'awardee_raw_name',   label: 'Awardee',           numeric: false },
  { id: 'nse_symbol',         label: 'Symbol',            numeric: false },
  { id: 'order_value_cr',     label: 'Order ₹Cr',         numeric: true },
  { id: 'project_description',label: 'Project',           numeric: false },
  { id: 'stage',              label: 'Stage',             numeric: false },
  { id: 'state',              label: 'State',             numeric: false },
  { id: 'sector',             label: 'Sector',            numeric: false },
  { id: 'lodr_match_status',  label: 'LODR Match',        numeric: false },
  { id: 'lodr_lag_hours',     label: 'LODR Lag',          numeric: true },
  { id: 'attachment_url',     label: 'PDF',               numeric: false },
  { id: 'portal_link',        label: 'Portal',            numeric: false },
];

const PreLODRTab = ({ readOnly = false } = {}) => {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [edge, setEdge] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState('ALL');
  const [stage, setStage] = useState('ALL');
  const [matchStatus, setMatchStatus] = useState('ALL');
  const [sortKey, setSortKey] = useState('detected_at');
  const [sortDir, setSortDir] = useState('desc');
  // 147j+17t — Scraper Debug panel state
  const [debugLog, setDebugLog] = useState([]);
  const [debugStatus, setDebugStatus] = useState({});
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugBusy, setDebugBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (source !== 'ALL') params.set('source', source);
      if (stage !== 'ALL') params.set('stage', stage);
      if (matchStatus !== 'ALL') params.set('match_status', matchStatus);
      const [ev, sm, ed] = await Promise.all([
        axios.get(`${API}/api/pre-lodr/events?${params}`, { headers: authHeaders() }),
        axios.get(`${API}/api/pre-lodr/summary`, { headers: authHeaders() }),
        axios.get(`${API}/api/pre-lodr/historical-edge`, { headers: authHeaders() }),
      ]);
      setEvents(ev.data?.events || []);
      setSummary(sm.data || null);
      setEdge(ed.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Pre-LODR');
    } finally {
      setLoading(false);
    }
  }, [source, stage, matchStatus]);

  useEffect(() => { load(); }, [load]);

  const runScrape = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/pre-lodr/scrape`, null, { headers: authHeaders() });
      const total = r.data?.total_inserted ?? 0;
      toast.success(`Scrapers: ${total} events upserted across ${Object.keys(r.data?.per_source || {}).length} sources`);
      await load();
      await loadDebug();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  // 147j+17t — Scraper Debug helpers.
  const loadDebug = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/pre-lodr/debug-log?limit=40`, { headers: authHeaders() });
      setDebugLog(r.data?.log || []);
      setDebugStatus(r.data?.source_status || {});
    } catch (e) { /* silent */ }
  }, []);

  const refetchOne = async (src) => {
    setDebugBusy((prev) => ({ ...prev, [src]: true }));
    try {
      const r = await axios.post(`${API}/api/admin/pre-lodr/debug-fetch/${encodeURIComponent(src)}`, null, { headers: authHeaders() });
      toast.success(`${src}: parsed ${r.data?.parsed ?? 0}, inserted ${r.data?.inserted ?? 0}`);
      await loadDebug();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setDebugBusy((prev) => ({ ...prev, [src]: false })); }
  };

  useEffect(() => { if (!readOnly && debugOpen) loadDebug(); }, [debugOpen, readOnly, loadDebug]);

  const runMatcher = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/pre-lodr/match?lookback_hours=168`, null, { headers: authHeaders() });
      toast.success(`Matcher: ${r.data.matched} newly matched / ${r.data.scanned} scanned`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const runSeed = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/pre-lodr/seed`, null, { headers: authHeaders() });
      toast.success(`Seed: ${r.data.inserted_or_updated} events upserted`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const handleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  const sorted = useMemo(() => {
    const arr = [...events];
    arr.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return arr;
  }, [events, sortKey, sortDir]);

  return (
    <div className="space-y-4" data-testid="pre-lodr-tab">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-zinc-100 flex items-center gap-2">
            <Radar size={20} className="text-blue-400" /> Pre-LODR Intelligence
          </h1>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            Public tender-portal awards — spotted BEFORE the listed company files LODR.
            Information only · L1 is preliminary · LoA is stronger · Contract Signed is final.
          </div>
        </div>
        {!readOnly && (
          <div className="flex gap-2 items-center">
            <button
              type="button"
              onClick={runScrape}
              disabled={busy}
              className="px-3 py-1.5 text-xs rounded border border-emerald-500/60 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-100 transition disabled:opacity-40 font-mono"
              data-testid="prelodr-scrape-btn"
            >Run scrapers now</button>
            <button
              type="button"
              onClick={runMatcher}
              disabled={busy}
              className="px-3 py-1.5 text-xs rounded border border-zinc-700 hover:border-blue-400/60 hover:text-blue-200 transition disabled:opacity-40 font-mono"
              data-testid="prelodr-match-btn"
            >Run LODR matcher</button>
            <button
              type="button"
              onClick={runSeed}
              disabled={busy}
              className="px-3 py-1.5 text-xs rounded border border-blue-500/60 bg-blue-500/15 hover:bg-blue-500/25 text-blue-100 transition disabled:opacity-40 font-mono"
              data-testid="prelodr-seed-btn"
            >Seed synthetic (dev)</button>
          </div>
        )}
      </div>

      {/* KPI strip */}
      <KpiStrip summary={summary} events={sorted} />

      {/* Historical Edge widget — average lead-time by source */}
      {edge.length > 0 && (
        <div className="rounded border border-zinc-800 bg-zinc-950/40 p-3" data-testid="prelodr-historical-edge">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
              Historical edge — avg lead-time before LODR
            </div>
            <div className="text-[9px] text-zinc-600 italic">
              (only counts matched events · positive = we spotted it first)
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {edge.map((r) => {
              const avg = r.avg_lag_h;
              const positive = avg != null && avg > 0;
              const tone = avg == null
                ? 'bg-zinc-900/60 text-zinc-400 border-zinc-700'
                : positive
                  ? 'bg-emerald-500/10 text-emerald-100 border-emerald-500/40'
                  : 'bg-rose-500/10 text-rose-100 border-rose-500/40';
              return (
                <div
                  key={r.source}
                  className={`rounded border p-2 ${tone}`}
                  data-testid={`prelodr-edge-${r.source}`}
                  title={`${r.count} matched events · min ${fmtLag(r.min_lag_h)} · max ${fmtLag(r.max_lag_h)} · ₹${(r.total_cr || 0).toFixed(0)} Cr total`}
                >
                  <div className="text-[9px] font-mono uppercase tracking-wider opacity-70">{r.source}</div>
                  <div className="text-sm font-bold font-mono">
                    {avg == null ? '—' : (avg > 0 ? '+' : '') + avg.toFixed(1) + 'h'}
                  </div>
                  <div className="text-[9px] opacity-70">{r.count} matched · min {fmtLag(r.min_lag_h)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center text-xs">
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Filters:</div>
        <select
          value={source} onChange={(e) => setSource(e.target.value)}
          className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-200 font-mono"
          data-testid="prelodr-source-select"
        >
          {SOURCES.map((s) => <option key={s} value={s}>{s === 'ALL' ? 'All sources' : s}</option>)}
        </select>
        <select
          value={stage} onChange={(e) => setStage(e.target.value)}
          className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-200 font-mono"
          data-testid="prelodr-stage-select"
        >
          {STAGES.map((s) => <option key={s} value={s}>{s === 'ALL' ? 'All stages' : s}</option>)}
        </select>
        <select
          value={matchStatus} onChange={(e) => setMatchStatus(e.target.value)}
          className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-200 font-mono"
          data-testid="prelodr-match-select"
        >
          {MATCH_STATUS.map((s) => <option key={s} value={s}>{s === 'ALL' ? 'All match status' : s}</option>)}
        </select>
        <div className="text-[10px] text-zinc-500 ml-auto font-mono">
          {loading ? 'loading…' : `${sorted.length} event${sorted.length === 1 ? '' : 's'} shown`}
        </div>
      </div>

      {/* Table */}
      <div className="rounded border border-zinc-800 bg-zinc-950/40 overflow-x-auto" data-testid="prelodr-events-table">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-zinc-800 text-zinc-500 uppercase text-[9px] tracking-wider">
              {HEADERS.map((h) => (
                <th
                  key={h.id}
                  className={`px-2 py-1.5 ${h.numeric ? 'text-right' : 'text-left'} cursor-pointer hover:text-zinc-200 select-none whitespace-nowrap`}
                  onClick={() => handleSort(h.id)}
                  data-testid={`prelodr-th-${h.id}`}
                >
                  {h.label}
                  {sortKey === h.id && (
                    <span className="ml-0.5 inline-flex align-middle">
                      {sortDir === 'asc' ? <ArrowUp size={9} /> : <ArrowDown size={9} />}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="prelodr-events-tbody">
            {sorted.length === 0 && (
              <tr>
                <td colSpan={HEADERS.length} className="py-8 text-center text-zinc-500 italic">
                  No Pre-LODR events yet. Scrapers publish here as they detect public awards
                  on NHAI, PowerGrid, NTPC, MoD, SECI, IREPS portals.
                </td>
              </tr>
            )}
            {sorted.map((e) => (
              <tr key={e._id} className="border-b border-zinc-900 hover:bg-zinc-900/40 transition" data-testid={`prelodr-row-${e._id}`}>
                <td className="px-2 py-1.5 text-zinc-400 font-mono text-[10px] whitespace-nowrap">{fmtDT(e.detected_at)}</td>
                <td className="px-2 py-1.5 font-mono text-blue-300">{e.source}</td>
                <td className="px-2 py-1.5 text-zinc-300 max-w-[180px] truncate" title={e.customer || ''}>{e.customer || '—'}</td>
                <td className="px-2 py-1.5 text-zinc-200 max-w-[220px] truncate" title={e.awardee_raw_name || ''}>{e.awardee_raw_name || '—'}</td>
                <td className="px-2 py-1.5 font-mono font-semibold">
                  {e.nse_symbol
                    ? <span className="text-zinc-100" data-testid={`prelodr-symbol-${e._id}`}>{e.nse_symbol}</span>
                    : <span className="text-zinc-500 italic text-[10px]">unmapped</span>}
                </td>
                <td className="px-2 py-1.5 text-right font-mono text-zinc-100">{fmtCr(e.order_value_cr)}</td>
                <td className="px-2 py-1.5 text-zinc-300 max-w-[280px] truncate" title={e.project_description || ''}>
                  {e.project_description || '—'}
                </td>
                <td className="px-2 py-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase ${STAGE_TONE[e.stage] || 'bg-zinc-800 text-zinc-300'}`} data-testid={`prelodr-stage-${e._id}`}>
                    {e.stage}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-zinc-400 text-[10px]">{e.state || '—'}</td>
                <td className="px-2 py-1.5 text-zinc-400 text-[10px]">{e.sector || '—'}</td>
                <td className="px-2 py-1.5 text-[10px] whitespace-nowrap" data-testid={`prelodr-match-${e._id}`}>
                  {MATCH_ICON[e.lodr_match_status] || null}
                  <span className="ml-1 text-zinc-400">{e.lodr_match_status}</span>
                </td>
                <td className="px-2 py-1.5 text-right font-mono text-zinc-400 text-[10px]">{fmtLag(e.lodr_lag_hours)}</td>
                <td className="px-2 py-1.5">
                  {e.attachment_url
                    ? <a href={e.attachment_url} target="_blank" rel="noreferrer" className="text-blue-300 hover:underline flex items-center gap-0.5 text-[10px]">
                        PDF <ExternalLink size={9} />
                      </a>
                    : <span className="text-zinc-600 text-[10px]">—</span>}
                </td>
                <td className="px-2 py-1.5">
                  {e.portal_link
                    ? <a href={e.portal_link} target="_blank" rel="noreferrer" className="text-blue-300 hover:underline flex items-center gap-0.5 text-[10px]">
                        Site <ExternalLink size={9} />
                      </a>
                    : <span className="text-zinc-600 text-[10px]">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 147j+17t — Scraper Debug Panel (admin only) */}
      {!readOnly && (
        <div className="rounded border border-zinc-800 bg-zinc-950/40" data-testid="prelodr-debug-panel">
          <button
            type="button"
            onClick={() => setDebugOpen(!debugOpen)}
            className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-zinc-900/60 transition"
            data-testid="prelodr-debug-toggle"
          >
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
              Scraper Debug · last {debugLog.length} fetch attempts
            </div>
            <div className="text-[10px] font-mono text-zinc-500">{debugOpen ? '▲ hide' : '▼ show'}</div>
          </button>
          {debugOpen && (
            <div className="border-t border-zinc-800 p-3 space-y-2">
              <div className="text-[10px] text-zinc-500 italic">
                Records every scraper HTTP call: status, bytes, first 2000 chars of body, parse count.
                Use this to diagnose why a source returns 0 events (IP blocked? URL 404? selector missed?).
              </div>
              {['NHAI','PowerGrid','MoD','SECI','IREPS'].map((src) => {
                const entries = debugLog.filter((e) => e.source === src);
                const last = entries[0];
                const status = debugStatus[src] || {};
                const statusTone = ({
                  'URL_OUTDATED':   'bg-amber-500/20 text-amber-200',
                  'IP_BLOCKED':     'bg-rose-500/20 text-rose-200',
                  'BOT_DETECTION':  'bg-orange-500/20 text-orange-200',
                  'CAPTCHA_GATED':  'bg-purple-500/20 text-purple-200',
                  'WORKING':        'bg-emerald-500/20 text-emerald-200',
                })[status.known_status] || 'bg-zinc-800/50 text-zinc-500';
                const badgeTone =
                  !last ? 'bg-zinc-800/50 text-zinc-500' :
                  last.error ? 'bg-rose-500/20 text-rose-200' :
                  last.http_status !== 200 ? 'bg-amber-500/20 text-amber-200' :
                  (last.parsed_count > 0 ? 'bg-emerald-500/20 text-emerald-200' : 'bg-blue-500/20 text-blue-200');
                return (
                  <div key={src} className="rounded border border-zinc-800 bg-black/30 p-2" data-testid={`prelodr-debug-src-${src}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-xs font-mono font-semibold text-zinc-100 w-24">{src}</div>
                      {status.known_status && (
                        <span
                          className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${statusTone}`}
                          title={status.note || ''}
                          data-testid={`prelodr-debug-status-${src}`}
                        >
                          {status.known_status}
                        </span>
                      )}
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${badgeTone}`}>
                        {!last ? 'no fetches' :
                          last.error ? `err: ${last.error.split(':')[0]}` :
                          `HTTP ${last.http_status} · ${last.bytes}b · ${last.elapsed_ms}ms · parsed ${last.parsed_count ?? '?'}`}
                      </span>
                      <button
                        type="button"
                        onClick={() => refetchOne(src)}
                        disabled={debugBusy[src]}
                        className="ml-auto px-2 py-0.5 text-[10px] rounded border border-zinc-700 hover:border-blue-400/60 hover:text-blue-200 disabled:opacity-40 font-mono"
                        data-testid={`prelodr-debug-refetch-${src}`}
                      >
                        {debugBusy[src] ? '…' : 'Refetch now'}
                      </button>
                    </div>
                    {status.note && (
                      <div className="mt-1 text-[10px] text-zinc-500 italic pl-2 border-l-2 border-zinc-800">
                        {status.note}
                      </div>
                    )}
                    {last && (
                      <details className="mt-1">
                        <summary className="text-[10px] text-zinc-500 cursor-pointer">
                          {last.url} · {new Date(last.ts).toLocaleTimeString('en-IN')}
                        </summary>
                        <pre className="mt-1 text-[9px] whitespace-pre-wrap break-all bg-black/40 text-zinc-300 p-2 rounded max-h-40 overflow-auto font-mono">
                          {last.body_head || '(empty body)'}
                        </pre>
                      </details>
                    )}
                  </div>
                );
              })}
              <div className="pt-1 flex gap-2">
                <button
                  type="button"
                  onClick={loadDebug}
                  className="px-2 py-1 text-[10px] rounded border border-zinc-700 hover:border-zinc-500 text-zinc-300 font-mono"
                  data-testid="prelodr-debug-refresh"
                >
                  Refresh debug log
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Legal footnote */}
      <div className="text-[10px] text-zinc-600 italic max-w-3xl leading-snug">
        Sources: public tender portals (NHAI, PowerGrid, NTPC, MoD, SECI, IREPS). All information
        is legally scrapeable from public domains. This feed is informational — L1 status is not
        a binding award and can be cancelled or renegotiated. Do not act on any single row without
        cross-verifying against the underlying portal link.
      </div>
    </div>
  );
};

export default PreLODRTab;
